import React, { useState } from 'react';
import moment from 'moment';
import groupBy from 'lodash/groupBy';

import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Card,
  Flex,
  Text,
  Skeleton,
  useDisclosure,
  Spinner,
} from '@workshop/ui';

import { uiAction } from 'redux/actions/common';

import { HeaderTag } from 'components/AppHeader';
import { UserAvatar } from 'components/UserAvatar';

import { Dispatch } from 'types';
// Import shared types
import { JournalUnitSummary, CohortMember } from './types';

import { JournalEntryModal } from '.';

// Original styling functions
const getBackgroundColor = (status: 'active' | 'upcoming' | 'expired') => {
  switch (status) {
    case 'active':
      return 'green.100';
    case 'upcoming':
      return 'orange.100';
    default:
      return 'red.100';
  }
};

const getColor = (status: 'active' | 'upcoming' | 'expired') => {
  switch (status) {
    case 'active':
      return 'green.600';
    case 'upcoming':
      return 'orange.600';
    default:
      return 'red.600';
  }
};

// Original MemberCardProps using imported CohortMember
interface MemberCardProps extends CohortMember {
  currentUserName: string;
  onClickAssessment?: (userName: string, cohortId: number) => void;
  onClickUserActivity?: (
    cohortId: number,
    username: string,
    subcategory: number
  ) => void;
  cohortId: number;
  dispatch: Dispatch;
}

// Original MemberCard component structure and styling
const MemberCard: React.FC<MemberCardProps> = ({
  // Destructure props from CohortMember
  id: memberId,
  avatar,
  assessmentCount,
  name,
  username,
  isLoading,
  privateMessageTopicId,
  discourseCategoryId,
  // Other props
  cohortId,
  currentUserName,
  onClickAssessment,
  onClickUserActivity,
  dispatch,
}) => {
  return (
    <Box
      key={`member-${username}-${memberId}`}
      marginY={1}
      paddingX={1}
      w={['100%', '100%', '100%', '100%', '100%', '50%']}
    >
      <Skeleton isLoaded={!isLoading}>
        <Card
          flexDirection={{ base: 'column', md: 'row' }}
          alignItems={{ base: 'flex-start', md: 'center' }}
          boxShadow="md"
          borderRadius={{ base: 'sm', md: 'xl' }}
        >
          <Flex alignItems="center" flex={1} mb={{ base: 2, md: 0 }}>
            <UserAvatar
              avatarPicture={avatar} // Correct prop
              name={name}
              size="2xs"
              userId={memberId}
            />
            <Flex
              flex={1}
              ml={2}
              minW={0}
              cursor="pointer"
              onClick={() =>
                onClickUserActivity &&
                discourseCategoryId &&
                onClickUserActivity(cohortId, username, discourseCategoryId)
              }
            >
              <Text
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                overflow="hidden"
              >
                {name}
              </Text>
            </Flex>
          </Flex>
          <Flex
            width={{ base: '100%', sm: 'auto' }}
            flexDirection={{ base: 'column', sm: 'row' }}
            alignItems={{ base: 'flex-start', sm: 'normal', md: 'center' }}
          >
            <Button
              width={{ base: '100%', sm: 'auto' }}
              secondary
              icon={privateMessageTopicId ? 'Chat' : 'AddCircle'}
              size="sm"
              fontSize="xs"
              onClick={() => {
                dispatch(
                  uiAction.toggleUserMessaging({
                    userId: memberId,
                    title: name,
                    username: username,
                    isExpanded: true,
                  })
                );
              }}
            >
              {privateMessageTopicId ? 'Messages' : 'New Message'}
            </Button>
            {Boolean(discourseCategoryId) && (
              <Button
                width={{ base: '100%', sm: 'auto' }}
                secondary
                icon="Collections"
                size="sm"
                fontSize="xs"
                ml={{ base: 0, sm: 2 }}
                mt={{ base: 2, sm: 0 }}
                onClick={() =>
                  onClickUserActivity &&
                  discourseCategoryId &&
                  onClickUserActivity(cohortId, username, discourseCategoryId)
                }
              >
                Posts
              </Button>
            )}
            {assessmentCount && assessmentCount > 0 ? (
              <Button
                width={{ base: '100%', sm: 'auto' }}
                secondary
                icon="ContentPaste"
                size="sm"
                fontSize="xs"
                ml={{ base: 0, sm: 2 }}
                mt={{ base: 2, sm: 0 }}
                onClick={() =>
                  onClickAssessment && onClickAssessment(username, cohortId)
                }
              >
                {`${assessmentCount} Assessment${
                  assessmentCount > 1 ? 's' : ''
                }`}
              </Button>
            ) : null}
          </Flex>
        </Card>
      </Skeleton>
    </Box>
  );
};

// Original Cohort interface using imported types
interface Cohort {
  id: number;
  members: CohortMember[];
  journalUnits: JournalUnitSummary[];
  onExpandCohort: () => Promise<void>;
  startDate: string;
  status: 'active' | 'upcoming' | 'expired';
  statusDescription?: string;
  discourseCategoryId: number | null;
  parentDiscourseCategoryId?: number;
  discourseGroupChatTopicId?: number;
  label: string;
  isLoading?: boolean;
  assessmentsLoading?: boolean;
}

// Original CohortItemProps
interface CohortItemProps extends Cohort {
  currentUserName: string;
  groupChatTitle?: string;
  onClickAssessment?: (userName: string, cohortId: number) => void;
  onClickAllActivity?: (cohortId: number, subcategory: number) => void;
  onClickUserActivity?: (
    cohortId: number,
    username: string,
    subcategory: number
  ) => void;
  dispatch: Dispatch;
  index: number; // Needed for border logic
}

// Original CohortItem component structure and logic
const CohortItem: React.FC<CohortItemProps> = ({
  members,
  journalUnits,
  status,
  startDate,
  statusDescription,
  id: cohortId,
  discourseCategoryId,
  parentDiscourseCategoryId,
  discourseGroupChatTopicId,
  label,
  currentUserName,
  groupChatTitle,
  onClickAssessment,
  onClickAllActivity,
  onClickUserActivity,
  dispatch,
  isLoading,
  assessmentsLoading,
  onExpandCohort, // Still needed for Accordion onChange handler below
  index,
}) => {
  // Journal Entries Modal disclosure
  const { isOpen, onOpen, onClose } = useDisclosure();

  // useEffect for onExpandCohort is handled by Accordion's onChange

  return (
    <AccordionItem
      key={`accordion-item-${cohortId}`}
      borderTop={index === 0 ? 0 : 'default'}
    >
      {(
        { isExpanded } // Use render prop from AccordionItem
      ) => (
        <>
          <AccordionButton display="flex" p={3}>
            <Flex flex={1}>
              <HeaderTag
                title={status} // Correct prop
                bg={getBackgroundColor(status)}
                color={getColor(status)}
                mr={3}
                minW="75px"
                justifyContent="center"
              />
              <Text textAlign="left">
                {`${moment(startDate).format('Do MMMM YYYY')}${
                  label ? ` - ${label}` : ''
                }`}
              </Text>
              {status === 'active' && statusDescription && (
                <Text color="text.muted" flex={1} ml={4} textAlign="left">
                  {statusDescription}
                </Text>
              )}
            </Flex>
            <Text
              color="text.muted"
              pr={1}
              display={{ base: 'none', sm: 'inline' }}
            >
              {isExpanded ? 'Collapse' : 'Expand'}
            </Text>
            <AccordionIcon color="text.muted" />
          </AccordionButton>
          <AccordionPanel padding={2} backgroundColor="background.tint1">
            {assessmentsLoading ? (
              <Flex flex={1} justifyContent="center" alignItems="center" my={2}>
                <Spinner color="text.muted" size="sm" mr={2} />
                <Text fontSize="sm" color="text.muted">
                  Learner assessments loading...
                </Text>
              </Flex>
            ) : null}
            <Flex flexWrap="wrap">
              {isExpanded // Conditionally render members based on isExpanded
                ? members.map((m) => (
                    <MemberCard
                      {...m} // Spread member props
                      key={`member-card-${cohortId}-${m.id}`}
                      onClickAssessment={onClickAssessment}
                      onClickUserActivity={onClickUserActivity}
                      currentUserName={currentUserName}
                      cohortId={cohortId}
                      dispatch={dispatch}
                    />
                  ))
                : null}
            </Flex>
            <Flex
              flexDirection={{ base: 'column', md: 'row' }}
              justifyContent="flex-end"
              p={2}
              pt={3}
            >
              {discourseGroupChatTopicId ? (
                <Button
                  isLoading={isLoading}
                  icon="Chat"
                  size="sm"
                  onClick={() =>
                    discourseGroupChatTopicId &&
                    dispatch(
                      uiAction.toggleUserMessaging({
                        discourseGroupTopicId: discourseGroupChatTopicId,
                        title: groupChatTitle || '',
                        isExpanded: true,
                      })
                    )
                  }
                >
                  Open Class Discussion
                </Button>
              ) : null}
              {discourseCategoryId ? (
                <Button
                  isLoading={isLoading}
                  icon="Collections"
                  size="sm"
                  ml={{ base: 0, md: 2 }}
                  mt={{ base: 2, md: 0 }}
                  onClick={() =>
                    onClickAllActivity &&
                    onClickAllActivity(cohortId, discourseCategoryId)
                  }
                >
                  View All Post Activity
                </Button>
              ) : null}
              <Button
                isLoading={isLoading || assessmentsLoading}
                icon="TableChart"
                size="sm"
                ml={{ base: 0, md: 2 }}
                mt={{ base: 2, md: 0 }}
                onClick={onOpen} // Open the JournalEntryModal
              >
                See Table of Uploads
              </Button>
            </Flex>
          </AccordionPanel>
          {/* Render modal outside the conditional panel rendering */}
          <JournalEntryModal
            isOpen={isOpen}
            onClose={onClose}
            members={members}
            journalUnits={journalUnits}
            parentDiscourseCategoryId={parentDiscourseCategoryId}
          />
        </>
      )}
    </AccordionItem>
  );
};

// Original CohortsCardProps
interface CohortsCardProps {
  cohorts: Cohort[];
  currentUserName: string;
  courseTitle?: string;
  onClickAssessment?: (userName: string, cohortId: number) => void;
  onClickAllActivity?: (cohortId: number, subcategory: number) => void;
  onClickUserActivity?: (
    cohortId: number,
    username: string,
    subcategory: number
  ) => void;
  isLoading?: boolean;
  dispatch: Dispatch;
}

// Original CohortsCard component structure and logic
const CohortsCard: React.FC<CohortsCardProps> = ({
  courseTitle = undefined,
  cohorts,
  currentUserName,
  isLoading,
  onClickAssessment,
  onClickAllActivity,
  onClickUserActivity,
  dispatch,
}) => {
  const [expandedItemIndexes, setExpandedItemIndexes] = useState<number[]>([]);
  const [showExpiredCohorts, setShowExpiredCohorts] = useState<boolean>(false);

  // Original Accordion logic
  const cohortsByStatus = groupBy(cohorts, 'status');
  // Define allCohorts based on current state for the onChange handler
  const allCohortsForExpansionLogic = [
    ...(cohortsByStatus.active || []),
    ...(cohortsByStatus.upcoming || []),
    ...(showExpiredCohorts ? cohortsByStatus.expired || [] : []),
  ];

  return (
    <Card padding={0}>
      {isLoading ? (
        <Skeleton isLoaded={false} w="100%" margin={2}>
          <Box h="40px" w="100%" />
        </Skeleton>
      ) : (
        <Accordion
          allowToggle
          flex="1"
          index={expandedItemIndexes} // Control accordion state
          onChange={(expandedIdx) => {
            // Logic to handle multiple vs single index correctly
            const expandedIndexes = Array.isArray(expandedIdx)
              ? expandedIdx
              : typeof expandedIdx === 'number'
              ? [expandedIdx]
              : [];

            // Find the *newly* expanded cohort index relative to allCohortsForExpansionLogic
            const newlyExpandedIndex = expandedIndexes.find(
              (idx) => !expandedItemIndexes.includes(idx)
            );

            // Get the actual cohort object if an index was found
            const expandedCohort =
              typeof newlyExpandedIndex === 'number'
                ? allCohortsForExpansionLogic[newlyExpandedIndex]
                : undefined;

            // Trigger expand action if a new item was expanded
            if (expandedCohort) {
              expandedCohort.onExpandCohort();
            }
            // Update the state with the new list of expanded indexes
            setExpandedItemIndexes(expandedIndexes);
          }}
        >
          {/* Map over active and upcoming */}
          {cohortsByStatus.active?.map((c, idx) => (
            <CohortItem
              key={c.id}
              {...c}
              index={idx} // Pass index
              currentUserName={currentUserName}
              groupChatTitle={courseTitle}
              onClickAssessment={onClickAssessment}
              onClickAllActivity={onClickAllActivity}
              onClickUserActivity={onClickUserActivity}
              dispatch={dispatch}
            />
          ))}
          {cohortsByStatus.upcoming?.map((c, idx) => (
            <CohortItem
              key={c.id}
              {...c}
              index={idx + (cohortsByStatus.active?.length || 0)}
              currentUserName={currentUserName}
              groupChatTitle={courseTitle}
              onClickAssessment={onClickAssessment}
              onClickAllActivity={onClickAllActivity}
              onClickUserActivity={onClickUserActivity}
              dispatch={dispatch}
            />
          ))}
          {/* Button to toggle expired cohorts */}
          {cohortsByStatus.expired?.length > 0 ? (
            <>
              <Flex borderTop="default" p={3}>
                <Button
                  secondary
                  size="xs"
                  onClick={() => setShowExpiredCohorts(!showExpiredCohorts)}
                  icon="History"
                >
                  {`${
                    showExpiredCohorts
                      ? 'Hide'
                      : `Show ${cohortsByStatus.expired.length}`
                  } Expired Class${
                    cohortsByStatus.expired.length === 1 ? '' : 'es'
                  }`}
                </Button>
              </Flex>
              {/* Conditionally render expired cohorts */}
              {showExpiredCohorts
                ? cohortsByStatus.expired.map((c, idx) => (
                    <CohortItem
                      key={c.id}
                      {...c}
                      index={
                        idx +
                        (cohortsByStatus.active?.length || 0) +
                        (cohortsByStatus.upcoming?.length || 0)
                      }
                      currentUserName={currentUserName}
                      groupChatTitle={courseTitle}
                      onClickAssessment={onClickAssessment}
                      onClickAllActivity={onClickAllActivity}
                      onClickUserActivity={onClickUserActivity}
                      dispatch={dispatch}
                    />
                  ))
                : null}
            </>
          ) : null}
        </Accordion>
      )}
    </Card>
  );
};

export default CohortsCard;
